import { useCallback } from "react";
import { useStore } from "react-redux";

import { useAppDispatch } from "app/redux-store-interactions";
import { type RootState } from "app/store";
import { measurePickers } from "components/toolbars/implementations/pickerGroups";
import { bottomToolbarActions } from "components/toolbars/mobile/slice";
import { coordinatesActions, selectCoordinatesEditSessionStartSnapshot } from "features/coordinates";
import { measureActions, selectObjectMeasureEditSessionStartSnapshot } from "features/measure/measureSlice";
import { mobileMeasureActions, selectMobileMeasure } from "features/mobileMeasure/mobileMeasureSlice";
import { clippingOutlineLaserActions, selectLaserEditSessionStartSnapshot } from "features/outlineLaser";
import { pointLineActions, selectCurrentPointLine, selectPointLineEditSessionStartSnapshot } from "features/pointLine";
import { renderActions, selectClippingEditSessionStartSnapshot, selectPicker } from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";

/**
 * Returns a confirm callback that commits in-session changes by clearing the edit-session
 * snapshots, then exits the current mode.
 * Used by ToolModeConfirmationToolbar's Confirm button, the Enter shortcut, and BottomToolbar's Save.
 */
export function useConfirmCurrentModeChanges() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();

    return useCallback(() => {
        const state = store.getState();
        const picker = selectPicker(state);
        const activeToolMode = selectActiveToolMode(state);
        const isActive = activeToolMode !== ProcoreToolMode.Default;
        if (!isActive) {
            return;
        }

        const laserSnapshotBefore = selectLaserEditSessionStartSnapshot(state);
        const pointLineSnapshotBefore = selectPointLineEditSessionStartSnapshot(state);
        const coordinatesSnapshotBefore = selectCoordinatesEditSessionStartSnapshot(state);
        const clippingSnapshotBefore = selectClippingEditSessionStartSnapshot(state);
        const objectMeasureSnapshotBefore = selectObjectMeasureEditSessionStartSnapshot(state);
        const isMeasurePicker = measurePickers.includes(picker);
        const mobileMeasureBefore = isMeasurePicker ? selectMobileMeasure(state) : null;
        const currentPointLine = selectCurrentPointLine(state);

        const apply = () => {
            if (isMeasurePicker) {
                dispatch(mobileMeasureActions.reset());
            }
            dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Default));
            dispatch(renderActions.stopPicker(picker));

            if (currentPointLine.points.length > 0) {
                dispatch(pointLineActions.newPointLine());
            }

            dispatch(clippingOutlineLaserActions.clearEditSessionStartSnapshot());
            dispatch(pointLineActions.clearEditSessionStartSnapshot());
            dispatch(coordinatesActions.clearEditSessionStartSnapshot());
            // Dropping the snapshot without restoring it is what keeps the measurement on screen.
            dispatch(measureActions.clearObjectMeasureEditSessionStartSnapshot());
            dispatch(renderActions.clearClippingEditSessionStartSnapshot());
            dispatch(bottomToolbarActions.setActiveTab("tools"));
        };

        apply();

        dispatch(
            undoRedoActions.push({
                description: "Confirm current mode changes",
                undoCallback: () => {
                    dispatch(procoreUxActions.setActiveToolMode(activeToolMode));
                    dispatch(renderActions.setPicker(picker));
                    dispatch(clippingOutlineLaserActions.setEditSessionStartSnapshot(laserSnapshotBefore));
                    dispatch(pointLineActions.setEditSessionStartSnapshot(pointLineSnapshotBefore));
                    dispatch(coordinatesActions.setEditSessionStartSnapshot(coordinatesSnapshotBefore));
                    dispatch(measureActions.setObjectMeasureEditSessionStartSnapshot(objectMeasureSnapshotBefore));
                    dispatch(renderActions.setClippingEditSessionStartSnapshot(clippingSnapshotBefore));
                    if (mobileMeasureBefore) {
                        dispatch(mobileMeasureActions.setState(mobileMeasureBefore));
                    }
                    dispatch(bottomToolbarActions.expand());
                },
                redoCallback: apply,
            }),
        );
    }, [dispatch, store]);
}
